import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';

interface StepItemProps {
  number: number;
  title: string;
  description: string;
}

/**
 * Step item component with numbered circle, title, and description
 * Used for "How It Works" and similar step-by-step sections
 */
export const StepItem: React.FC<StepItemProps> = ({ number, title, description }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        p: 2,
        borderRadius: 2,
        transition: theme.transitions.create(['transform', 'background-color'], {
          duration: theme.transitions.duration.short,
        }),
        // Active state for touch feedback
        '&:active': {
          transform: 'scale(0.98)',
          bgcolor: 'action.hover',
        },
      }}
    >
      {/* Numbered Circle */}
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2,
          boxShadow: (theme) =>
            theme.palette.mode === 'light'
              ? '0 4px 16px rgba(255, 154, 118, 0.35)'
              : '0 4px 16px rgba(255, 154, 118, 0.25)',
        }}
      >
        <Typography
          variant="h6"
          sx={{
            color: 'white',
            fontWeight: 700,
          }}
        >
          {number}
        </Typography>
      </Box>

      {/* Title */}
      <Typography
        variant="h6"
        component="h3"
        sx={{
          fontWeight: 600,
          mb: 1,
        }}
      >
        {title}
      </Typography>

      {/* Description */}
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          lineHeight: 1.7,
          maxWidth: 280,
        }}
      >
        {description}
      </Typography>
    </Box>
  );
};
